import Papa from 'papaparse'
import ProductDB from '../../database/wrappers/product';
import { convertTrybuImport } from '../../types/fiscal';
import tabela_ncm from '../fiscal/nfce/tabela_ncm.json';

const officialNcmCodes = new Set(
  tabela_ncm.Nomenclaturas.map(item => item.Codigo.replace(/\D/g, ''))
)

class ImportProductCsv {

  constructor(
    private readonly estabId: string
  ){}

  async import(file: File): Promise<{ updated: number, created: 0, removed: 0 }> {
    console.info('[ImportProductCsv] Iniciando importação', {
      operationMode: 'UPDATE_ONLY',
      fileName: file?.name,
      fileSize: file?.size,
      establishmentId: this.estabId,
    })

    try {
      const array = await this.startReaderFile(file)
      this.validateData(array)
      this.validateUniqueIds(array)

      const productDb = new ProductDB(this.estabId)
      const existingProducts = await productDb.getAll()
      const existingById = new Map(existingProducts.map((product: any) => [product.id, product]))
      const notFound = array.filter(item => !existingById.has(item.id))

      if (notFound.length) {
        console.error('[ImportProductCsv] Importação cancelada; IDs do CSV não encontrados', {
          productsNotFound: notFound.length,
          notFoundIds: notFound.map(item => item.id),
        })
        throw new Error(`Importação cancelada: ${notFound.length} ID(s) do CSV não existem no estabelecimento`)
      }

      const updates = array.map(item => {
        const existingProduct: any = existingById.get(item.id)
        return {
          id: item.id,
          data: {
            fiscal_data: {
              ...(existingProduct.fiscal_data || {}),
              ...item.fiscal_data,
            },
          },
        }
      })

      console.info('[ImportProductCsv] CSV validado; iniciando atualização fiscal', {
        operationMode: 'UPDATE_ONLY',
        productsToUpdate: updates.length,
        productsToCreate: 0,
        productsToRemove: 0,
      })

      await productDb.updateBatch(updates)

      console.info('[ImportProductCsv] Atualização fiscal concluída', {
        productsUpdated: updates.length,
        productsCreated: 0,
        productsRemoved: 0,
      })

      return { updated: updates.length, created: 0, removed: 0 }
    } catch (error) {
      console.error('[ImportProductCsv] Falha na importação', error)
      throw error
    }
  }

  private startReaderFile(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(ImportProductCsv.readCSV(reader.result))
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(new Error('Não foi possível ler o arquivo CSV'))
      reader.readAsText(file);
    })
  }

  private static readCSV(csv: string | ArrayBuffer | null): any[] {
    if (typeof csv !== 'string') throw new Error('O conteúdo do arquivo CSV é inválido')

    const results = Papa.parse<Record<string, any>>(csv, {
      header: true,
      skipEmptyLines: true,
    })

    if (results.errors.length) {
      console.warn('[ImportProductCsv] Avisos encontrados ao interpretar o CSV', results.errors)
    }

    const normalizedHeaders = new Set((results.meta.fields || []).map(field => this.normalizeKey(field)))
    const isLegacyCbsIbsLayout = !normalizedHeaders.has('CLASSIFICACAO TRIBUTARIA CBS/IBS')
      && normalizedHeaders.has('COD BENEFICIO FISCAL')
      && normalizedHeaders.has('CST CBS/IBS')

    const datas = results.data.reduce<any[]>((products, row, index) => {
      const formatedRow = this.__convertKeys(row)

      const productName = this.cleanValue(formatedRow.nome)
      const originalNcm = this.cleanValue(formatedRow.ncm)
      const normalizedNcm = this.normalizeNcm(originalNcm)
      const legacyClassification = isLegacyCbsIbsLayout
        ? this.normalizeCode(formatedRow.cBenef, 6)
        : undefined

      if (normalizedNcm && normalizedNcm !== originalNcm) {
        console.info('[ImportProductCsv] NCM corrigido automaticamente', {
          line: index + 2,
          product: productName,
          originalNcm,
          normalizedNcm,
        })
      }

      const fiscalData = this.removeUndefinedValues({
        bar_code: this.normalizeOptionalText(formatedRow.codBarra),
        ncm: normalizedNcm,
        cst: this.normalizeNumericCode(formatedRow.cst, 2),
        csosn: this.normalizeNumericCode(formatedRow.csosn, 2),
        cest: this.normalizeNumericCode(formatedRow.cest, 7),
        cfop: this.normalizeNumericCode(formatedRow.cfop, 4),
        aliq_icms: this.normalizeDecimal(formatedRow.icms),
        cBenef: isLegacyCbsIbsLayout ? undefined : this.normalizeOptionalText(formatedRow.cBenef),
        tributary_situation: this.parseTributarySituation(formatedRow.situacaoTrib),
        pis: this.normalizeDecimal(formatedRow.pis),
        cofins: this.normalizeDecimal(formatedRow.cofins),
        cClassTrib: legacyClassification || this.normalizeCode(formatedRow.cClassTrib, 6),
        cst_cbs_ibs: this.normalizeCode(formatedRow.cst_cbs_ibs, 3),
        percentualReducao: this.normalizeDecimal(formatedRow.percentualReducao),
      })

      products.push({
        id: this.cleanValue(formatedRow.id),
        name: productName,
        fiscal_data: fiscalData,
      })
      return products
    }, [])

    console.info('[ImportProductCsv] CSV interpretado', {
      rowsRead: results.data.length,
      productsConverted: datas.length,
      headers: results.meta.fields,
      legacyCbsIbsLayoutDetected: isLegacyCbsIbsLayout,
      ignoredFieldsPreserveExistingDatabaseValues: true,
    })
    return datas
  }

  private validateData(datas: any[]) {
    if (!datas.length) throw new Error('Nenhum produto válido foi encontrado no arquivo CSV')

    const ignoredFiscalFields: { line: number, product: string, field: string, value: any, reason: string }[] = []
    const ignoreInvalidField = (item: any, row: number, field: string, reason: string) => {
      ignoredFiscalFields.push({ line: row, product: item.name, field, value: item.fiscal_data[field], reason })
      delete item.fiscal_data[field]
    }

    datas.forEach((item, index) => {
      const row = index + 2
      if (!item.id) throw new Error(`Linha ${row}: campo ID ausente`)
      if (!item.name) throw new Error(`Linha ${row}: campo nome ausente`)

      if (item.fiscal_data.ncm !== undefined
        && (!/^\d{8}$/.test(item.fiscal_data.ncm)
          || (!officialNcmCodes.has(item.fiscal_data.ncm) && item.fiscal_data.ncm !== '00000000'))) {
        ignoreInvalidField(item, row, 'ncm', 'NCM inválido ou inexistente; valor atual preservado')
      }
      if (item.fiscal_data.cst !== undefined && !/^\d{2}$/.test(item.fiscal_data.cst)) {
        ignoreInvalidField(item, row, 'cst', 'CST inválido; valor atual preservado')
      }
      if (item.fiscal_data.csosn !== undefined && !/^\d{2,3}$/.test(item.fiscal_data.csosn)) {
        ignoreInvalidField(item, row, 'csosn', 'CSOSN inválido; valor atual preservado')
      }
      if (item.fiscal_data.cest !== undefined && !/^\d{7}$/.test(item.fiscal_data.cest)) {
        ignoreInvalidField(item, row, 'cest', 'CEST inválido; valor atual preservado')
      }
      if (item.fiscal_data.cfop !== undefined && !/^\d{4}$/.test(item.fiscal_data.cfop)) {
        ignoreInvalidField(item, row, 'cfop', 'CFOP inválido; valor atual preservado')
      }
      if (item.fiscal_data.tributary_situation !== undefined
        && ![0, 1, 2].includes(item.fiscal_data.tributary_situation)) {
        ignoreInvalidField(item, row, 'tributary_situation', 'Situação tributária inválida; valor atual preservado')
      }
      if (item.fiscal_data.cClassTrib !== undefined && !/^\d{6}$/.test(item.fiscal_data.cClassTrib)) {
        ignoreInvalidField(item, row, 'cClassTrib', 'Classificação Tributária CBS/IBS inválida; valor atual preservado')
      }
      if (item.fiscal_data.cst_cbs_ibs !== undefined && !/^\d{3}$/.test(item.fiscal_data.cst_cbs_ibs)) {
        ignoreInvalidField(item, row, 'cst_cbs_ibs', 'CST CBS/IBS inválido; valor atual preservado')
      }

      ;['aliq_icms', 'pis', 'cofins', 'percentualReducao'].forEach(field => {
        const value = item.fiscal_data[field]
        if (value === undefined) return
        const numberValue = Number(value)
        if (!Number.isFinite(numberValue) || numberValue < 0 || numberValue > 100) {
          ignoreInvalidField(item, row, field, 'Percentual inválido; valor atual preservado')
        }
      })
    })

    if (ignoredFiscalFields.length) {
      console.warn('[ImportProductCsv] Campos fiscais inválidos ignorados; valores atuais serão preservados', {
        ignoredFields: ignoredFiscalFields.length,
        affectedProducts: new Set(ignoredFiscalFields.map(item => item.product)).size,
        details: ignoredFiscalFields,
      })
    }
  }

  private validateUniqueIds(datas: any[]) {
    const seenIds = new Set<string>()
    const duplicateIds = new Set<string>()
    datas.forEach(item => {
      if (seenIds.has(item.id)) duplicateIds.add(item.id)
      seenIds.add(item.id)
    })

    if (duplicateIds.size) {
      console.error('[ImportProductCsv] Importação cancelada; IDs repetidos no CSV', {
        duplicateIds: Array.from(duplicateIds),
      })
      throw new Error(`Importação cancelada: ${duplicateIds.size} ID(s) repetido(s) no CSV`)
    }
  }

  private static normalizeKey(value: string): string {
    return value
      .replace(/^\uFEFF/, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9/]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
  }

  private static cleanValue(value: any): string {
    return value == null ? '' : String(value).trim()
  }

  private static cleanOptionalValue(value: any): string {
    const cleaned = this.cleanValue(value)
    const normalized = this.normalizeKey(cleaned)
    return ['X', 'N/I', 'NI', 'NAO INFORMADO'].includes(normalized) ? '' : cleaned
  }

  private static normalizeOptionalText(value: any): string | undefined {
    return this.cleanOptionalValue(value) || undefined
  }

  private static normalizeDecimal(value: any): string | undefined {
    const cleaned = this.cleanOptionalValue(value)
    return cleaned ? cleaned.replace(',', '.') : undefined
  }

  private static normalizeCode(value: any, length: number): string | undefined {
    const cleaned = this.cleanOptionalValue(value).replace(/^'/, '').replace(/^="(.*)"$/, '$1')
    if (!cleaned) return undefined
    return /^\d+$/.test(cleaned) ? cleaned.padStart(length, '0') : cleaned
  }

  private static normalizeNumericCode(value: any, minimumLength: number): string | undefined {
    return this.normalizeCode(value, minimumLength)
  }

  private static normalizeNcm(value: any): string | undefined {
    const cleaned = this.cleanOptionalValue(value).replace(/^'/, '').replace(/^="(.*)"$/, '$1')
    if (!cleaned || /^0{1,6}$/.test(cleaned)) return undefined

    const digits = /^[\d.\s/-]+$/.test(cleaned) ? cleaned.replace(/\D/g, '') : cleaned
    if (!/^\d{7}$/.test(digits)) return digits

    const candidates = new Set<string>()
    for (let index = 0; index <= digits.length; index++) {
      const candidate = `${digits.slice(0, index)}0${digits.slice(index)}`
      if (officialNcmCodes.has(candidate)) candidates.add(candidate)
    }
    if (candidates.size === 1) return Array.from(candidates)[0]
    return `0${digits}`
  }

  private static removeUndefinedValues(values: Record<string, any>): Record<string, any> {
    return Object.entries(values).reduce<Record<string, any>>((result, [key, value]) => {
      if (value !== undefined) result[key] = value
      return result
    }, {})
  }

  private static parseTributarySituation(value: any): number | undefined {
    const cleaned = this.cleanOptionalValue(value)
    if (!cleaned) return undefined
    const normalized = this.normalizeKey(cleaned).toLowerCase()
    if (['0', '1', '2'].includes(normalized)) return Number(normalized)
    if (['i', 't', 's'].includes(normalized)) return convertTrybuImport(normalized)
    return Number.NaN
  }

  private static __convertKeys(inputObject: Record<string, any>) {
    const keyMapping: Record<string, string> = {
      'ID': 'id',
      'IMAGEM': 'nome',
      'NOME': 'nome',
      'PRECO': 'preco',
      'DESCRICAO': 'descricao',
      'COD INTERNO': 'descricao',
      'CODIGO INTERNO': 'descricao',
      'CODIGO SECUNDARIO': 'codSecondary',
      'CODIGO EXTERNO': 'codSecondary',
      'SUGESTAO': 'insumo',
      'STATUS': 'status',
      'UNIDADE': 'unidade',
      'COD BARRA': 'codBarra',
      'NCM': 'ncm',
      'CST': 'cst',
      'CSOSN': 'csosn',
      'CEST': 'cest',
      'CFOP': 'cfop',
      'ICMS': 'icms',
      'COD BENEFICIO FISCAL': 'cBenef',
      'CODIGO DE BENEFICIO FISCAL': 'cBenef',
      'SIT TRIBUTARIA': 'situacaoTrib',
      'PIS': 'pis',
      'COFINS': 'cofins',
      'CLASSIFICACAO TRIBUTARIA CBS/IBS': 'cClassTrib',
      'CST CBS/IBS': 'cst_cbs_ibs',
      'PERCENTUAL DE REDUCAO': 'percentualReducao',
    }

    return Object.entries(inputObject).reduce<Record<string, any>>((output, [key, value]) => {
      const mappedKey = keyMapping[this.normalizeKey(key)]
      if (mappedKey) output[mappedKey] = value
      return output
    }, {})
  }
}

export default ImportProductCsv;
